import React, { useState, useEffect, useCallback } from 'react';
import { getCurrentSession } from '../auth/cognito';
import './EmailClient.css';

const API = process.env.REACT_APP_API_URL || 'https://api.stratosstandard.com';

const FOLDERS = [
  { id: 'INBOX', label: 'Inbox' },
  { id: 'Sent',  label: 'Sent'  },
  { id: 'Trash', label: 'Trash' },
];

const TrashIcon    = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>;
const ReplyIcon    = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/></svg>;
const RefreshIcon  = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>;

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (now - d < 7 * 24 * 60 * 60 * 1000) {
    return d.toLocaleDateString([], { weekday: 'short' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function extractEmailAddress(from) {
  const match = from?.match(/<(.+?)>/);
  return match ? match[1] : from || '';
}

export default function EmailClient() {
  const [folder, setFolder]         = useState('INBOX');
  const [messages, setMessages]     = useState([]);
  const [selectedMsg, setSelected]  = useState(null);
  const [composing, setComposing]   = useState(false);
  const [loading, setLoading]       = useState(false);
  const [msgLoading, setMsgLoading] = useState(false);
  const [sending, setSending]       = useState(false);
  const [error, setError]           = useState(null);
  const [unreadInbox, setUnreadInbox] = useState(0);
  const [composeForm, setCompose]   = useState({ to: '', subject: '', body: '' });
  const [noAccount, setNoAccount]   = useState(false);

  const authHeaders = useCallback(async () => {
    const session = await getCurrentSession();
    return { Authorization: `Bearer ${session.idToken}` };
  }, []);

  const loadMessages = useCallback(async (f) => {
    setLoading(true);
    setError(null);
    setSelected(null);
    setNoAccount(false);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/email/messages?folder=${encodeURIComponent(f)}`, { headers });
      const data = await res.json();
      if (res.status === 404) { setNoAccount(true); return; }
      if (!res.ok) throw new Error(data.error || 'Failed to load messages');
      setMessages(data.messages || []);
      if (f === 'INBOX') {
        setUnreadInbox((data.messages || []).filter(m => !m.seen).length);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { loadMessages(folder); }, [folder]); // eslint-disable-line react-hooks/exhaustive-deps

  async function openMessage(msg) {
    setComposing(false);
    if (selectedMsg?.uid === msg.uid) return;
    setMsgLoading(true);
    setSelected({ ...msg, _loading: true });
    try {
      const headers = await authHeaders();
      const res = await fetch(
        `${API}/email/messages/${msg.uid}?folder=${encodeURIComponent(folder)}`,
        { headers }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load message');
      setSelected(data);
      if (!msg.seen) {
        setMessages(prev => prev.map(m => m.uid === msg.uid ? { ...m, seen: true } : m));
        setUnreadInbox(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      setError(err.message);
      setSelected(null);
    } finally {
      setMsgLoading(false);
    }
  }

  async function deleteMessage(uid) {
    try {
      const headers = await authHeaders();
      await fetch(`${API}/email/messages/${uid}?folder=${encodeURIComponent(folder)}`, {
        method: 'DELETE',
        headers,
      });
      setMessages(prev => prev.filter(m => m.uid !== uid));
      if (selectedMsg?.uid === uid) setSelected(null);
    } catch (err) {
      setError(err.message);
    }
  }

  async function toggleRead(msg, e) {
    e.stopPropagation();
    const newSeen = !msg.seen;
    setMessages(prev => prev.map(m => m.uid === msg.uid ? { ...m, seen: newSeen } : m));
    setUnreadInbox(prev => Math.max(0, prev + (newSeen ? -1 : 1)));
    try {
      const headers = await authHeaders();
      await fetch(`${API}/email/messages/${msg.uid}`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ seen: newSeen, folder }),
      });
    } catch (err) {
      // Revert optimistic update
      setMessages(prev => prev.map(m => m.uid === msg.uid ? { ...m, seen: !newSeen } : m));
      setUnreadInbox(prev => Math.max(0, prev + (newSeen ? 1 : -1)));
    }
  }

  async function sendEmail() {
    if (!composeForm.to || !composeForm.subject) return;
    setSending(true);
    setError(null);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/email/send`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: composeForm.to, subject: composeForm.subject, text: composeForm.body }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send');
      setComposing(false);
      setCompose({ to: '', subject: '', body: '' });
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  }

  function startReply() {
    setCompose({
      to: extractEmailAddress(selectedMsg.from),
      subject: selectedMsg.subject.match(/^Re:/i) ? selectedMsg.subject : `Re: ${selectedMsg.subject}`,
      body: '',
    });
    setComposing(true);
  }

  if (noAccount) {
    return (
      <div className="email-no-account">
        <p>No email account linked.</p>
        <p>Contact the administrator to have your <strong>@stratosstandard.com</strong> mailbox configured.</p>
      </div>
    );
  }

  return (
    <div className="email-client">
      {/* ── Folder sidebar ── */}
      <div className="email-folders">
        <button
          className="email-compose-btn"
          onClick={() => { setComposing(true); setSelected(null); setCompose({ to: '', subject: '', body: '' }); }}
        >
          + Compose
        </button>

        <nav className="email-folder-nav">
          {FOLDERS.map(f => (
            <button
              key={f.id}
              className={`email-folder-item${folder === f.id ? ' active' : ''}`}
              onClick={() => { setFolder(f.id); loadMessages(f.id); }}
            >
              <span>{f.label}</span>
              {f.id === 'INBOX' && unreadInbox > 0 && (
                <span className="email-unread-badge">{unreadInbox}</span>
              )}
            </button>
          ))}
        </nav>

        <button className="email-refresh-btn" onClick={() => loadMessages(folder)}>
          <RefreshIcon /> Refresh
        </button>
      </div>

      {/* ── Message list ── */}
      <div className="email-message-list">
        {error && <div className="email-list-error">{error}</div>}
        {loading ? (
          <div className="email-list-empty">Loading…</div>
        ) : messages.length === 0 && !error ? (
          <div className="email-list-empty">No messages</div>
        ) : (
          messages.map(msg => (
            <div
              key={msg.uid}
              className={`email-message-row${!msg.seen ? ' unread' : ''}${selectedMsg?.uid === msg.uid ? ' selected' : ''}`}
              onClick={() => openMessage(msg)}
            >
              <div className="email-row-top">
                <span
                  className="email-row-dot"
                  title={msg.seen ? 'Mark as unread' : 'Mark as read'}
                  onClick={e => toggleRead(msg, e)}
                />
                <span className="email-row-from">{msg.from || '(unknown)'}</span>
                <span className="email-row-date">{fmtDate(msg.date)}</span>
              </div>
              <div className="email-row-subject">{msg.subject}</div>
            </div>
          ))
        )}
      </div>

      {/* ── Viewer / Compose pane ── */}
      <div className="email-viewer">
        {composing ? (
          <div className="email-compose">
            <div className="email-compose-header">
              <span>New Message</span>
              <button className="email-compose-close" onClick={() => setComposing(false)}>×</button>
            </div>
            <div className="email-compose-fields">
              <div className="email-compose-field">
                <label>To</label>
                <input
                  type="email"
                  value={composeForm.to}
                  onChange={e => setCompose(p => ({ ...p, to: e.target.value }))}
                  placeholder="recipient@example.com"
                  autoFocus
                />
              </div>
              <div className="email-compose-field">
                <label>Subject</label>
                <input
                  type="text"
                  value={composeForm.subject}
                  onChange={e => setCompose(p => ({ ...p, subject: e.target.value }))}
                  placeholder="Subject"
                />
              </div>
            </div>
            <textarea
              className="email-compose-body"
              value={composeForm.body}
              onChange={e => setCompose(p => ({ ...p, body: e.target.value }))}
              placeholder="Write your message…"
            />
            <div className="email-compose-footer">
              {error && <span className="email-compose-error">{error}</span>}
              <button
                className="email-send-btn"
                onClick={sendEmail}
                disabled={sending || !composeForm.to || !composeForm.subject}
              >
                {sending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        ) : selectedMsg ? (
          <div className="email-view">
            <div className="email-view-header">
              <div className="email-view-subject">{selectedMsg.subject}</div>
              <button
                className="email-view-action-btn"
                title="Delete"
                onClick={() => deleteMessage(selectedMsg.uid)}
              >
                <TrashIcon />
              </button>
            </div>
            <div className="email-view-meta">
              <div><span className="email-meta-label">From</span>{selectedMsg.from}</div>
              <div><span className="email-meta-label">To</span>{selectedMsg.to}</div>
              <div><span className="email-meta-label">Date</span>{selectedMsg.date ? new Date(selectedMsg.date).toLocaleString() : ''}</div>
            </div>
            {msgLoading ? (
              <div className="email-view-loading">Loading…</div>
            ) : (
              <div className={`email-view-body${selectedMsg.html ? ' email-view-body--html' : ''}`}>
                {selectedMsg.html ? (
                  <iframe
                    title="email-body"
                    className="email-view-iframe"
                    srcDoc={selectedMsg.html}
                    sandbox="allow-same-origin allow-popups"
                  />
                ) : (
                  <pre className="email-view-text">{selectedMsg.text || '(empty message)'}</pre>
                )}
              </div>
            )}
            <div className="email-view-actions">
              <button className="email-view-action-btn email-reply-btn" onClick={startReply}>
                <ReplyIcon /> Reply
              </button>
            </div>
          </div>
        ) : (
          <div className="email-viewer-empty">Select a message to read</div>
        )}
      </div>
    </div>
  );
}
